import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import './DashboardCharts.css';

export default function DashboardCharts({ terms = [], error = false, onOpenPending }) {
    const [selection, setSelection] = useState(null);
    const [supportModalOpen, setSupportModalOpen] = useState(false);
    const closeButtonRef = useRef(null);
    const chartsRef = useRef(null);
    const hasSelectedTermRef = useRef(false);
    const latest = [...terms].reverse().find(term => term.has_data)
        || [...terms].reverse().find(term => term.marking_progress?.total > 0);
    const selectedTerm = selection ?? Number(latest?.term || 1);
    const data = terms.find(term => Number(term.term) === selectedTerm);
    const progress = data?.marking_progress;
    const total = Number(progress?.total || 0);
    const completed = Math.min(total, Math.max(0, Number(progress?.completed || 0)));
    const outstanding = total - completed;
    const percentage = total ? Math.round(completed / total * 100) : 0;
    const subjects = [...(data?.subjects || [])].sort((a, b) => `${a.name} ${a.class_name || ''}`.localeCompare(`${b.name} ${b.class_name || ''}`));
    const supportLearners = [...(data?.support_learners || [])]
        .sort((a, b) => Number(a.average_marks) - Number(b.average_marks) || `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`));
    const trendData = terms.map((term) => {
        const totalMarks = Number(term.marking_progress?.total || 0);
        const completedMarks = Number(term.marking_progress?.completed || 0);
        return {
            term: `Term ${term.term}`,
            average: term.average_marks == null ? null : Number(term.average_marks.toFixed(1)),
            completion: totalMarks > 0 ? Number((completedMarks / totalMarks * 100).toFixed(1)) : null
        };
    });
    const hasTrendData = trendData.some((term) => term.average !== null || term.completion !== null);

    useEffect(() => {
        if (!supportModalOpen) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setSupportModalOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeButtonRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [supportModalOpen]);

    useLayoutEffect(() => {
        if (!hasSelectedTermRef.current) {
            hasSelectedTermRef.current = true;
            return undefined;
        }
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
            return undefined;
        }
        const context = gsap.context(() => {
            const bars = chartsRef.current?.querySelectorAll('.dashboard-progress-track > span, .dashboard-average-track > span');
            if (!bars?.length) return;
            gsap.fromTo(bars,
                { scaleX: 0.78, transformOrigin: 'left center' },
                { scaleX: 1, duration: 0.34, ease: 'power2.out', stagger: 0.035, overwrite: 'auto' }
            );
        }, chartsRef);
        return () => context.revert();
    }, [selectedTerm]);

    const renderSupportLearner = (learner) => {
        const subjectLabel = [learner.subject_name, learner.grade != null && learner.grade !== '' ? `Grade ${learner.grade}` : '', learner.class_name].filter(Boolean).join(' · ');
        return (
            <li key={`${learner.subject_id}-${learner.student_id}`}>
                <Link to={`/subjects/${learner.subject_id}?term=${selectedTerm}&student=${learner.student_id}`} className="dashboard-support-link" onClick={() => setSupportModalOpen(false)} aria-label={`Open ${learner.first_name} ${learner.last_name}'s ${learner.subject_name} marks`}>
                    <span className="dashboard-support-person"><strong>{learner.first_name} {learner.last_name}</strong><small>{subjectLabel}</small></span>
                    <span className="dashboard-support-score"><strong>{Number(learner.average_marks).toFixed(1)}%</strong><small>≤ {Number(learner.support_threshold).toFixed(0)}%</small></span>
                </Link>
            </li>
        );
    };

    return (
        <section className="dashboard-charts" ref={chartsRef} aria-labelledby="dashboard-charts-title">
            <div className="dashboard-charts-heading">
                <h2 id="dashboard-charts-title">Your term at a glance</h2>
                <label>Term
                    <select value={selectedTerm} onChange={event => setSelection(Number(event.target.value))}>
                        {[1, 2, 3, 4].map(term => <option key={term} value={term}>Term {term}</option>)}
                    </select>
                </label>
            </div>
            {error ? <p role="status" className="dashboard-chart-empty">Charts could not be loaded. Refresh the page to try again.</p> : (
                <>
                <div className="dashboard-charts-grid">
                    <article className="dashboard-chart-card">
                        <h3>Marking progress</h3>
                        <p className="dashboard-chart-caption">Individual mark entries for your classes</p>
                        {!progress ? <p className="dashboard-chart-empty">Marking progress is unavailable.</p> : total === 0 ? (
                            <p className="dashboard-chart-empty">No marks to enter for Term {selectedTerm} yet. Add learners and assessment sections to get started.</p>
                        ) : (
                            <>
                                <div className="dashboard-progress-summary"><strong>{percentage}%</strong><span>complete</span></div>
                                <div className="dashboard-progress-track" role="progressbar" aria-label={`Term ${selectedTerm} marking progress`} aria-valuemin={0} aria-valuemax={total} aria-valuenow={completed} aria-valuetext={`${completed} of ${total} marks entered`}>
                                    <span style={{ width: `${completed / total * 100}%` }} />
                                </div>
                                <div className="dashboard-progress-legend"><span><i />{completed.toLocaleString()} entered</span><span><i />{outstanding.toLocaleString()} outstanding</span></div>
                                {outstanding > 0 ? <button type="button" className="dashboard-chart-action" onClick={() => onOpenPending(selectedTerm)}>View pending marking <span aria-hidden="true">→</span></button>
                                    : <p className="dashboard-chart-complete">All marks entered for this term.</p>}
                            </>
                        )}
                    </article>
                    <article className="dashboard-chart-card">
                        <h3>Subject / class averages</h3>
                        <p className="dashboard-chart-caption">Based on entered marks · select a class to open it</p>
                        {subjects.length === 0 ? <p className="dashboard-chart-empty">No averages for Term {selectedTerm} yet. They’ll appear as you enter marks.</p> : (
                            <ul className="dashboard-average-list">
                                {subjects.map(subject => {
                                    const average = subject.average_marks == null ? null : Number(subject.average_marks);
                                    const valid = average !== null && Number.isFinite(average);
                                    const label = [subject.name, subject.grade != null && subject.grade !== '' ? `Grade ${subject.grade}` : '', subject.class_name].filter(Boolean).join(' · ');
                                    return <li key={subject.subject_id}>
                                        <Link to={`/subjects/${subject.subject_id}?term=${selectedTerm}`} className="dashboard-average-link">
                                            <span className="dashboard-average-label"><span>{label}</span><strong>{valid ? `${average.toFixed(1)}%` : 'No marks'}</strong></span>
                                            <span className="dashboard-average-track" aria-hidden="true"><span style={{ width: `${valid ? Math.max(0, Math.min(100, average)) : 0}%` }} /></span>
                                        </Link>
                                    </li>;
                                })}
                            </ul>
                        )}
                    </article>
                    <article className="dashboard-chart-card dashboard-support-card">
                        <div className="dashboard-support-heading">
                            <div>
                                <h3>Learners needing support</h3>
                                <p className="dashboard-chart-caption">At or below each subject’s support threshold</p>
                            </div>
                            {supportLearners.length > 0 && <span className="dashboard-support-count">{supportLearners.length}</span>}
                        </div>
                        {supportLearners.length === 0 ? (
                            <p className="dashboard-chart-empty">No learners need support for Term {selectedTerm} based on entered marks.</p>
                        ) : (
                            <ul className="dashboard-support-list">
                                {supportLearners.slice(0, 4).map(renderSupportLearner)}
                            </ul>
                        )}
                        {supportLearners.length > 0 && <button type="button" className="dashboard-support-all-button" onClick={() => setSupportModalOpen(true)}>View all learners <span aria-hidden="true">→</span></button>}
                    </article>
                </div>
                <article className="dashboard-trend-card">
                    <div>
                        <h3>Term performance trend</h3>
                        <p className="dashboard-chart-caption">Track average marks and marking completion across the year.</p>
                    </div>
                    {hasTrendData ? (
                        <div className="dashboard-line-chart" aria-label="Line graph of term averages and marking completion">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={trendData} margin={{ top: 8, right: 10, left: -18, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e4eee6" />
                                    <XAxis dataKey="term" tick={{ fill: '#607267', fontSize: 12 }} axisLine={false} tickLine={false} />
                                    <YAxis domain={[0, 100]} tick={{ fill: '#607267', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(value) => `${value}%`} />
                                    <Tooltip formatter={(value) => [`${value}%`]} contentStyle={{ borderRadius: 10, border: '1px solid #dce8df' }} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Line type="monotone" dataKey="average" name="Average mark" stroke="#28794a" strokeWidth={3} dot={{ r: 4 }} connectNulls />
                                    <Line type="monotone" dataKey="completion" name="Marking complete" stroke="#d49a36" strokeWidth={3} dot={{ r: 4 }} connectNulls />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : <p className="dashboard-chart-empty">Enter marks to start building your term trend.</p>}
                </article>
                </>
            )}
            {supportModalOpen && createPortal(
                <div className="dashboard-support-modal-overlay" onClick={() => setSupportModalOpen(false)}>
                    <section className="dashboard-support-modal" role="dialog" aria-modal="true" aria-labelledby="support-modal-title" onClick={event => event.stopPropagation()}>
                        <header>
                            <div><h2 id="support-modal-title">Learners needing support</h2><p>Term {selectedTerm} · {supportLearners.length} learners at or below their subject support threshold.</p></div>
                            <button ref={closeButtonRef} type="button" aria-label="Close learners needing support" onClick={() => setSupportModalOpen(false)}>×</button>
                        </header>
                        <ul className="dashboard-support-modal-list">{supportLearners.map(renderSupportLearner)}</ul>
                    </section>
                </div>
            , document.body)}
        </section>
    );
}
